import React, { Fragment, useState, useEffect, useContext } from "react";
import { notBlank } from "../../../util/ext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/pro-regular-svg-icons";
import {
  childrenHooks,
  getContentGroupsViewPath,
  IncludeDialogTypes,
  isAnthology,
  isCollection,
  isModule,
} from "../organizeUtil";
import { OrganizeContext } from "../organize-context";
import { equals, mergeRight } from "ramda";
import { useCurrentOrgId, useIsAdmin } from "../../../auth/authUtils";
import { useModuleDelete, useModuleUpdate } from "../../modules/moduleServices";
import {
  useAnthologyDelete,
  useAnthologyUpdate,
} from "../../anthologies/anthologyServices";
import {
  useCollectionDelete,
  useCollectionUpdate,
} from "../../collections/collectionServices";
import { useHistory, useLocation } from "react-router-dom";
import { GroupBrowserContext } from "../OrganizeView";

import Skeleton from "@material-ui/lab/Skeleton";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

import { BiblioboardLink } from "../../common/BiblioboardLink";
import DeleteDialog from "../../common/DeleteDialog";
import { useMediaPermissions } from "../../../util/mediaUtil";
import DeleteIconButton from "../../common/DeleteIconButton";
import { useAlert } from "../OrganizeProvider";
import { useCreateActiveModule } from "../../../manage-organizations/organizationServices";

const MoveUngroupedItem = ({
  action,
  group,
  moveActionIcon,
  moveActionTitle,
}) => {
  const context = useContext(OrganizeContext);
  const handleClick = () => {
    context.updateIncludeDialog({
      open: true,
      action: action,
      activeType: IncludeDialogTypes.contentGroup,
      selected: [group],
    });
  };
  return (
    <Tooltip title={moveActionTitle} placement="bottom">
      <IconButton onClick={handleClick}>
        <FontAwesomeIcon icon={moveActionIcon} />
      </IconButton>
    </Tooltip>
  );
};

const MoveGroupedItem = ({
  action,
  group,
  groupParent,
  moveActionIcon,
  moveActionTitle,
}) => {
  const context = useContext(OrganizeContext);
  const useChildren = childrenHooks[groupParent.mediaType];
  const children = useChildren(groupParent.id, { limit: "all" });
  const [parent, setParent] = useState({});
  useEffect(() => {
    if (children.isSuccess) {
      setParent({
        parent: groupParent,
        prevMediaList: equals(groupParent, "anthology")
          ? children.data.contents
          : children.data,
      });
    }
  }, [children.isSuccess, children.data, groupParent]);
  const handleClick = () => {
    context.updateIncludeDialog({
      open: true,
      action: action,
      activeType: IncludeDialogTypes.contentGroup,
      selected: [mergeRight(group, parent)],
    });
  };
  return children.isLoading ? (
    <Skeleton />
  ) : (
    <Tooltip title={moveActionTitle} placement="bottom">
      <IconButton onClick={handleClick}>
        <FontAwesomeIcon icon={moveActionIcon} />
      </IconButton>
    </Tooltip>
  );
};

const ContentGroupHeaderActions = props => {
  const isAdmin = useIsAdmin();
  const currentOrgId = useCurrentOrgId();
  const history = useHistory();
  const alert = useAlert();
  const groupBrowser = useContext(GroupBrowserContext);
  const { pathname, search } = useLocation();
  const { readOnly, deletable } = useMediaPermissions(props.group);

  const updateModule = useModuleUpdate();
  const createActveModule = useCreateActiveModule();
  const updateAnthology = useAnthologyUpdate();
  const updateCollection = useCollectionUpdate();
  const handleUndo = group => async () => {
    groupBrowser.setLoading(true);
    try {
      const mutation = {
        resourceIds: { id: group.id },
        data: {
          status: group?.status ?? null,
        },
      };

      if (isModule(group)) {
        await createActveModule.mutateAsync({
          resourceIds: { moduleId: group.id, orgId: group.orgId },
        });
        await updateModule.mutateAsync(mutation);
      }

      if (isAnthology(group)) {
        await updateAnthology.mutateAsync(mutation);
      }

      if (isCollection(group)) {
        await updateCollection.mutateAsync(mutation);
      }

      history.goBack();
    } catch (err) {
      alert.show("error", [err.message]);
    }
    groupBrowser.setLoading(false);
  };

  const deleteModule = useModuleDelete();
  const deleteAnthology = useAnthologyDelete();
  const deleteCollection = useCollectionDelete();
  const handleDelete = async () => {
    if (isModule(props.group)) {
      await deleteModule.mutateAsync({
        module: props.group,
        orgId: currentOrgId,
      });
    }

    if (isAnthology(props.group)) {
      await deleteAnthology.mutateAsync({ anthology: props.group });
    }

    if (isCollection(props.group)) {
      await deleteCollection.mutateAsync({ collection: props.group });
    }

    const home = getContentGroupsViewPath();
    history.push(home, {
      from: notBlank(search) ? pathname + search : pathname,
    });

    alert.update({
      actionButtonLabel: "undo",
      showAction: true,
      onAlertClick: handleUndo({ ...props.group, orgId: currentOrgId }),
    });
  };
  return (
    <Fragment>
      {notBlank(props.group) && !isModule(props.group) && (
        <Fragment>
          {!readOnly && (
            <Fragment>
              {notBlank(props.groupParent) ? (
                <MoveGroupedItem {...props} />
              ) : (
                <MoveUngroupedItem {...props} />
              )}
            </Fragment>
          )}
        </Fragment>
      )}

      {!readOnly && (
        <Tooltip title="Edit" placement="bottom">
          <IconButton onClick={props.handleEditClick}>
            <FontAwesomeIcon icon={faPen} />
          </IconButton>
        </Tooltip>
      )}

      {!isAdmin && deletable && (
        <DeleteDialog
          handleDelete={handleDelete}
          handleUndo={handleUndo}
          renderButton={onDelete => <DeleteIconButton onDelete={onDelete} />}
        />
      )}

      {notBlank(props.group) && <BiblioboardLink media={props.group} />}
    </Fragment>
  );
};

export default ContentGroupHeaderActions;
